import { logService } from '@nsftx/seven-client-retail-utils'
import { Core, Display } from '@nsftx/systems-sdk'
import DMDisplayError from '#src/classes/DMDisplayError'
import errorParser from '#src/common/error-handling/errorParser'

const LOG_PREFIX = '[deviceManagementDisplayService]'
let displayInstance: Display | null = null
let displayReady = false

const displayMessageHandlers = new Set<(payload: unknown) => void>()
let displayReceiveMessageListenerAttached = false

const dispatchDisplayMessage = (message: unknown) => {
  for (const handler of displayMessageHandlers) {
    handler(message)
  }
}

const ensureDisplayReceiveMessageListener = () => {
  if (displayReceiveMessageListenerAttached) {
    return
  }

  const display = getDisplayInstance()
  display.on('receive_message', dispatchDisplayMessage as (res: { data: unknown }) => void)
  displayReceiveMessageListenerAttached = true
}

const startKeepAlive = (appRunUuid: string) => {
  logService.info(`${LOG_PREFIX} Start DM appAlive pinging`, {
    code: 'T_DEVICE_MANAGEMENT_PING_START'
  })

  if (!appRunUuid) {
    logService.warn(`${LOG_PREFIX} AppRunUuid not set on ping DM set up`, {
      code: 'T_DEVICE_MANAGEMENT_KEEP_ALIVE_APP_RUN_UUID_MISSING'
    })
    return
  }

  const keepAlive = () => {
    displayInstance!
      .sendAppHeartbeatInterval(appRunUuid)
      .then(() => {
        logService.debug(
          `${LOG_PREFIX} Received success message from http appAlive request to DM`,
          {
            code: 'T_DEVICE_MANAGEMENT_KEEP_ALIVE_SUCCESS',
            details: {
              appRunUuid
            }
          }
        )
      })
      .catch((err) => {
        logService.error(`${LOG_PREFIX} Failed to send appAlive to DM`, {
          ...errorParser.parseUpstream(err),
          details: {
            appRunUuid
          },
          code: 'T_DEVICE_MANAGEMENT_KEEP_ALIVE_ERROR'
        })
      })
  }

  keepAlive()
  setInterval(keepAlive, 15000)
}

const initDmDisplay = (core: Core, appRunUuid: string) =>
  new Promise<void>((resolve, reject) => {
    const rejectTimeout = window.setTimeout(() => {
      reject(
        new DMDisplayError(
          `${LOG_PREFIX} Display instance did not receive ready event on init.`,
          'T_DEVICE_MANAGEMENT_DISPLAY_NOT_READY'
        )
      )
    }, 5000)
    displayInstance = new Display(core)
    displayReceiveMessageListenerAttached = false
    displayInstance.on('ready', () => {
      window.clearTimeout(rejectTimeout)
      displayReady = true
      logService.debug(`${LOG_PREFIX} Display module ready.`, {
        code: 'T_DEVICE_MANAGEMENT_DISPLAY_READY'
      })
      startKeepAlive(appRunUuid)
      resolve()
    })
  })

const getDisplayInstance = (): Display => {
  if (!displayInstance) {
    throw new DMDisplayError(
      `${LOG_PREFIX} Display instance is not initialized.`,
      'T_DEVICE_MANAGEMENT_DISPLAY_NOT_INIT'
    )
  }

  if (!displayReady) {
    throw new DMDisplayError(
      `${LOG_PREFIX} Display instance did not receive ready event.`,
      'T_DEVICE_MANAGEMENT_DISPLAY_NOT_READY'
    )
  }

  return displayInstance
}

const isDmDisplayReady = () =>
  new Promise<void>((resolve, reject) => {
    if (displayReady) {
      resolve()
    } else {
      reject(
        new DMDisplayError(
          `${LOG_PREFIX} Display instance not yet ready.`,
          'T_DEVICE_MANAGEMENT_DISPLAY_NOT_READY'
        )
      )
    }
  })

const switchDisplay = async (accountId: string) => {
  await isDmDisplayReady()
  const display = getDisplayInstance()
  const res = await display.BringAppToFront(accountId)
  return res
}

const getApplication = (appRunUuid: string) => {
  const display = getDisplayInstance()
  return display.getApplication(appRunUuid)
}

const forwardAppMessage = (type: string, payload?: unknown) => {
  const display = getDisplayInstance()
  display.appMessageForward({ type, payload })
}

/**
 * One `receive_message` listener on Display fans out to registered handlers.
 * Runtime passes the assembled app-message envelope (see systems-sdk Display._emitReceivedAppMessage).
 */
const subscribeDisplayMessages = <T>(handler: (payload: T) => void) => {
  ensureDisplayReceiveMessageListener()

  const wrappedHandler = (payload: unknown) => {
    handler(payload as T)
  }

  displayMessageHandlers.add(wrappedHandler)

  return () => {
    displayMessageHandlers.delete(wrappedHandler)
  }
}

export {
  initDmDisplay,
  switchDisplay,
  getDisplayInstance,
  getApplication,
  forwardAppMessage,
  subscribeDisplayMessages
}
